import React, { useMemo } from 'react';

import { type LanguageOption } from '@/src/utils';
import { DictionaryContext, usePage } from '@/src/app/hooks';

type DictionaryEntry = {
    EN: string;
    ES: string;
};

function translateMain(key: string, language: LanguageOption, fallback?: string) {
    const translations = mainDictionary.get(key);
    if (!translations) {
        if (fallback) {
            return fallback;
        }
        return `No translation found for ${key}`;
    }
    return translations[language];
}

export const DictionaryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { pageLanguage } = usePage();

    const translator = useMemo(() => {
        return {
            translate: (key: string, fallback?: string) =>
                translateMain(key, pageLanguage, fallback),
        };
    }, [pageLanguage]);
    return <DictionaryContext.Provider value={translator}>{children}</DictionaryContext.Provider>;
};

const mainDictionary = new Map<string, DictionaryEntry>();

mainDictionary.set('Activity Description', {
    EN: 'Activity Description',
    ES: 'Descripción de la Actividad',
});
mainDictionary.set('Activity Menu', { EN: 'Activity Menu', ES: 'Menú de Actividades SBC' });
mainDictionary.set('Activity Sheets', { EN: 'Activity Sheets', ES: 'Hojas de Actividades' });
mainDictionary.set('Additional Resources', {
    EN: 'Additional Resources',
    ES: 'Recursos Adicionales',
});
mainDictionary.set('Advance', { EN: 'Advance', ES: 'Avanzar' });
mainDictionary.set('All Grades', { EN: 'All Grades', ES: 'Todos los Grados' });
mainDictionary.set('Annual Diagnostic', { EN: 'Annual Diagnostic', ES: 'Diagnóstico Anual' });
mainDictionary.set('Assessments', { EN: 'Assessments', ES: 'Evaluaciones' });
mainDictionary.set('Back', { EN: 'Back', ES: 'Atrás' });
mainDictionary.set('Benchmark', { EN: 'Benchmark', ES: 'Exámene' });
mainDictionary.set('Benchmarks', { EN: 'Benchmarks', ES: 'Exámenes' });
mainDictionary.set('Benchmarks - Version A', {
    EN: 'Benchmarks - Version A',
    ES: 'Exámenes - Versión A',
});
mainDictionary.set('Benchmarks - Version B', {
    EN: 'Benchmarks - Version B',
    ES: 'Exámenes - Versión B',
});
mainDictionary.set('Beyond the Basic Facts', { EN: 'Beyond the Basic Facts', ES: 'BTBF' });
mainDictionary.set('Blackline Masters', {
    EN: 'Blackline Masters',
    ES: 'Origináles de Línea Negra',
});
mainDictionary.set('Blackline Masters List', {
    EN: 'Blackline Masters List',
    ES: 'Lista de Origináles de Línea Negra',
});
mainDictionary.set('BTBF', { EN: 'BTBF', ES: 'BTBF' });
mainDictionary.set('Calendar', { EN: 'Calendar', ES: 'Calendario' });
mainDictionary.set('Centers', { EN: 'Centers', ES: 'Centros' });
mainDictionary.set('Centers Description', {
    EN: 'Designed to strengthen fact fluency, deepen conceptual understanding, and build procedural skills across each unit. Unit centers feature a blend of hands-on activities and collaborative digital learning games.',
    ES: 'Diseñado para fortalecer la fluidez con las operaciones básicas, profundizar la comprensión conceptual y desarrollar habilidades procedimentales en cada unidad. Los centros de unidad incluyen una combinación de actividades prácticas y juegos digitales colaborativos.',
});
mainDictionary.set('Checklists', { EN: 'Checklists', ES: 'Listas de verificación' });
mainDictionary.set('Classroom', { EN: 'Classroom', ES: 'Aula' });
mainDictionary.set('Coming soon!', { EN: 'Coming soon!', ES: '¡Próximamente!' });
mainDictionary.set('Conceptual', { EN: 'Conceptual', ES: 'Conceptual' });
mainDictionary.set('Confirm New Password', {
    EN: 'Confirm New Password',
    ES: 'Confirmar Nueva Contraseña',
});
mainDictionary.set('Constructed Response', {
    EN: 'Constructed Response',
    ES: 'Respuesta Construida',
});
mainDictionary.set('Dashboard', { EN: 'Dashboard', ES: 'Panel' });
mainDictionary.set('Diagnostic Assessments', {
    EN: 'Diagnostic Assessments',
    ES: 'Evaluaciones Diagnósticas',
});
mainDictionary.set('eBooks', { EN: 'eBooks', ES: 'eBooks' });
mainDictionary.set('Elementary', { EN: 'Elementary', ES: 'Elemental' });
mainDictionary.set('Email', { EN: 'Email', ES: 'Correo Electrónico' });
mainDictionary.set('End of the Year', { EN: 'End of the Year', ES: 'Fin de año' });
mainDictionary.set('Extra Practice', { EN: 'Extra Practice', ES: 'Práctica Extra' });
mainDictionary.set('Grade', { EN: 'Grade', ES: 'Grado' });
mainDictionary.set('Grade Level', { EN: 'Grade Level', ES: 'Nivel de Grado' });
mainDictionary.set('Grades', { EN: 'Grades', ES: 'Grados' });
mainDictionary.set('Help', { EN: 'Help', ES: 'Ayuda' });
mainDictionary.set('Introduction', { EN: 'Introduction', ES: 'Introducción' });
mainDictionary.set('Investigation', { EN: 'Investigation', ES: 'Investigación' });
mainDictionary.set('Kindergarten', { EN: 'Kindergarten', ES: 'Kindergarten' });
mainDictionary.set('Learn More', { EN: 'Learn More', ES: 'Más Información' });
mainDictionary.set('Lesson', { EN: 'Lesson', ES: 'Lección' });
mainDictionary.set('Lessons', { EN: 'Lessons', ES: 'Lecciones' });
mainDictionary.set('Lesson Design', { EN: 'Lesson Design', ES: 'Diseño de Lección' });
mainDictionary.set('Math Tools', { EN: 'Math Tools', ES: 'Herramientas Matemáticas' });
mainDictionary.set('Middle School', { EN: 'Middle School', ES: 'Escuela Secundaria' });
mainDictionary.set('Mini Posters', { EN: 'Mini Posters', ES: 'Mini Carteles' });
mainDictionary.set('Miscellaneous', { EN: 'Miscellaneous', ES: 'Misceláneos' });
mainDictionary.set('My Lessons', { EN: 'My Lessons', ES: 'Mis Lecciones' });
mainDictionary.set('Name', { EN: 'Name', ES: 'Nombre' });
mainDictionary.set('Next Lesson', { EN: 'Next Lesson', ES: 'Lección Próxima' });
mainDictionary.set('New Password', { EN: 'New Password', ES: 'Nueva Contraseña' });
mainDictionary.set('Number Practice', { EN: 'Number Practice', ES: 'Práctica de Números' });
mainDictionary.set('Old Password', { EN: 'Old Password', ES: 'Contraseña Anterior' });
mainDictionary.set('Online Assessments', { EN: 'Online Assessments', ES: 'Evaluaciones en Línea' });
mainDictionary.set('Other', { EN: 'Other', ES: 'Otro' });
mainDictionary.set('Performance Task', { EN: 'Performance Task', ES: 'Tarea de Desempeño' });
mainDictionary.set('Performance Task 1', { EN: 'Performance Task 1', ES: 'Tarea de Desempeño 1' });
mainDictionary.set('Performance Task 2', { EN: 'Performance Task 2', ES: 'Tarea de Desempeño 2' });
mainDictionary.set('Performance Tasks', { EN: 'Performance Tasks', ES: 'Tareas de Desempeño' });
mainDictionary.set('Previous Lesson', { EN: 'Previous Lesson', ES: 'Lección anterior' });
mainDictionary.set('Professional Learning', {
    EN: 'Professional Learning',
    ES: 'Aprendizaje Profesional',
});
mainDictionary.set('Posters', { EN: 'Posters', ES: 'Carteles' });
mainDictionary.set('Pretests', { EN: 'Pretests', ES: 'Pre-Examens' });
mainDictionary.set('Primary', { EN: 'Primary', ES: 'Primario' });
mainDictionary.set('Problem Solving', { EN: 'Problem Solving', ES: 'Resolución de Problemas' });
mainDictionary.set('Procedural', { EN: 'Procedural', ES: 'Procedimental' });
mainDictionary.set('Progress Monitoring Tool', {
    EN: 'Progress Monitoring Tool',
    ES: 'Herramienta de Monitoreo de Progreso',
});
mainDictionary.set('Readiness', { EN: 'Readiness', ES: 'Preparación' });
mainDictionary.set('Reset', { EN: 'Reset', ES: 'Reiniciar' });
mainDictionary.set('Reset Password', { EN: 'Reset Password', ES: 'Restablecer Contraseña' });
mainDictionary.set('Resources', { EN: 'Resources', ES: 'Recursos' });
mainDictionary.set('Re-Engage', { EN: 'Re-Engage', ES: 'Volver a Comprometerse' });
mainDictionary.set('Sample Walls', { EN: 'Sample Walls', ES: 'Paredes de Muestra' });
mainDictionary.set('School District', { EN: 'School District', ES: 'Distrito Escolar' });
mainDictionary.set('School Name', { EN: 'School Name', ES: 'Nombre de la Escuela' });
mainDictionary.set('Standard', { EN: 'Standard', ES: 'Estándar' });
mainDictionary.set('Standards', { EN: 'Standards', ES: 'Estándares' });
mainDictionary.set('Standards Based Calendar', {
    EN: 'Standards Based Calendar',
    ES: 'Calendario Basado en Estándares',
});
mainDictionary.set('Start of the Year', { EN: 'Start of the Year', ES: 'Inicio del Año' });
mainDictionary.set('Strategy', { EN: 'Strategy', ES: 'Estrategia' });
mainDictionary.set('Student', { EN: 'Student', ES: 'Estudiante' });
mainDictionary.set('Student Edition', { EN: 'Student Edition', ES: 'Edición para Estudiantes' });
mainDictionary.set('Swun Math tech support', {
    EN: 'Swun Math tech support',
    ES: 'Soporte técnico de Swun Math',
});
mainDictionary.set('Task', { EN: 'Task', ES: 'Tarea' });
mainDictionary.set('STEM Task', { EN: 'STEM Task', ES: 'Tarea STEM' });
mainDictionary.set('Teacher', { EN: 'Teacher', ES: 'Profesor' });
mainDictionary.set('Teacher Edition', { EN: 'Teacher Edition', ES: 'Edición para Profesores' });
mainDictionary.set('Teacher Journal', { EN: 'Teacher Journal', ES: 'Diario del Maestro' });
mainDictionary.set('Teacher Manual Table of Contents', {
    EN: 'Teacher Manual Table of Contents',
    ES: 'Manual del Profesor TOC',
});
mainDictionary.set('Teacher Tools', { EN: 'Teacher Tools', ES: 'Herramientas para Profesores' });
mainDictionary.set('Unit', { EN: 'Unit', ES: 'Unidad' });
mainDictionary.set('Units', { EN: 'Units', ES: 'Unidades' });
mainDictionary.set('Unit Test', { EN: 'Unit Test', ES: 'Unidad Exámen' });
mainDictionary.set('Unit Tests', { EN: 'Unit Tests', ES: 'Unidad Exámenes' });
mainDictionary.set('Unit Tests - Version A', {
    EN: 'Unit Tests - Version A',
    ES: 'Unidad Exámenes - Versión A',
});
mainDictionary.set('Unit Tests - Version B', {
    EN: 'Unit Tests - Version B',
    ES: 'Unidad Exámenes - Versión B',
});
mainDictionary.set('Upper Elementary', { EN: 'Upper Elementary', ES: 'Primaria Superior' });
mainDictionary.set('User Guides', { EN: 'User Guides', ES: 'Guías de Usuario' });
mainDictionary.set('User Profile', { EN: 'User Profile', ES: 'Perfil de Usuario' });
mainDictionary.set('Version', { EN: 'Version', ES: 'Versión' });
mainDictionary.set('Versions', { EN: 'Versions', ES: 'Versiones' });
mainDictionary.set('View All Lessons', { EN: 'View All Lessons', ES: 'Ver Todas Las Lecciones' });
mainDictionary.set('Videos', { EN: 'Videos', ES: 'Videos' });
mainDictionary.set('View Assessments', { EN: 'View Assessments', ES: 'Ver Evaluaciones' });
mainDictionary.set('View PDF', { EN: 'View PDF', ES: 'Ver PDF' });
mainDictionary.set('Visit Classroom', { EN: 'Visit Classroom', ES: 'Visita el Aula' });
mainDictionary.set('Volume', { EN: 'Volume', ES: 'Volumen' });
mainDictionary.set('Your Account', { EN: 'Your Account', ES: 'Su Cuenta' });
